import React, { useEffect, useRef, useState } from 'react';
import { Spinner, Wizard, WizardProps } from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { ValidatePrerequisitesEditor } from 'src/components/common/checkout/validatePrerequisitesEditor';
import { DataAgreementsRequestContextEditor } from 'src/components/common/checkout/dataAgreementsRequestContextEditor';
import { ApprovalWorkflowsAndTicketsEditor } from 'src/components/common/checkout/approvalWorkflowsAndTicketsEditor';
import { AdditionalDetailsEditor } from 'src/components/common/checkout/additionalDetailsEditor';
import { CartAccessItem, TemplateMetadata } from 'src/components/common/checkout/common';
import { DataConsumerType, MatchedTemplateWorkflow } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import {
  shouldEnableApprovalContainer,
  shouldEnableSimContainer,
} from 'src/components/permissions/myPermissions/templateRequestDetailsContainer';
import { ARW_INFO_WIKI_PAGE, TEMPLATES_INFO_WIKI_PAGE } from 'src/components/templates/common';

export interface CheckoutWizardProps {
  cartItems: CartAccessItem[];
  activeGroup: string;
  activeWorkspace: any;
  // unique templates that matched.
  additionalMetadata: TemplateMetadata[];
  setAdditionalMetadata: (additionalMetadata: TemplateMetadata[]) => void;
  // all templates that matched, might have duplicate templateIds
  matchedTemplates: MatchedTemplateWorkflow[];
  username: string;
  useCase: string;
  setUseCase: (useCase: string) => void;
  dataConsumerType: DataConsumerType;
  setDataConsumerType: (value: DataConsumerType) => void;
  formSubmitted: boolean;
  onCancel: () => void;
  onSubmitAccessRequest: () => void;
  loading: boolean;
  requestSending: boolean;
}

export const CheckoutWizard = (props: CheckoutWizardProps) => {
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [wizardSteps, setWizardSteps] = useState<WizardProps.Step[]>([]);

  const [saveRequestContextLoading, setSaveRequestContextLoading] = useState<boolean>(false);

  // D3aValidation component passes error message back to here, if any.
  // This component will conditionally disable the next button (by overriding the navigate method) accordingly.
  // (The Next button cannot be disabled directly: https://cloudscape.aws.dev/components/button/?tabId=usage)
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>(undefined);

  // this makes it possible to call the save method within the RequestContextDetails component, from this component.
  const requestContextDetailComponentRef = useRef(null);

  useEffect(() => {
    const shouldEnableSIM = shouldEnableSimContainer(props.additionalMetadata);
    const shouldEnableApprovals = shouldEnableApprovalContainer(props.additionalMetadata);
    const shouldEnableDUA = props.additionalMetadata?.some(
      (temp) => temp.accessRequestWorkflows?.[0]?.dataAgreement?.agreementId != undefined,
    );
    const hasOptimisticApprovalEligibility = props.additionalMetadata?.some((template) =>
      template.accessRequestWorkflows?.some((arw) => arw.optimisticApprovalEnabled === true),
    );
    const steps: WizardProps.Step[] = [
      {
        title: 'Validate pre-requisites',
        info: undefined,
        description: 'Validate if the required pre-requisites are met before submitting access request.',
        errorText: validationErrorMessage,
        content: (
          <ValidatePrerequisitesEditor
            {...props}
            cartItems={props.cartItems}
            activeGroup={props.activeGroup}
            activeWorkspace={props.activeWorkspace}
            setValidationErrorMessage={setValidationErrorMessage}
          />
        ),
      },
    ];
    //set DUA
    if (shouldEnableDUA)
      steps.push({
        title: 'Data usage agreements',
        isOptional: false,
        info: (
          <Link variant='info' external={true} href={ARW_INFO_WIKI_PAGE + '#HDatausageagreements'}>
            Info
          </Link>
        ),
        description: 'Please fill out the data usage agreement(s) provided by the data owners.',
        content: (
          <DataAgreementsRequestContextEditor
            {...props}
            displaySaveButton={false}
            ref={requestContextDetailComponentRef}
            omniContext={{
              consumerAccountId: props.activeWorkspace?.accountId, // For now, checkout page is only used at workspace level
            }}
            hasOptimisticApprovalEligibility={hasOptimisticApprovalEligibility}
          />
        ),
      });
    if (shouldEnableSIM || shouldEnableApprovals) {
      steps.push({
        title: 'Approval workflows and tickets',
        isOptional: false,
        info: (
          <Link variant='info' external={true} href={TEMPLATES_INFO_WIKI_PAGE + '#HCreateanApprovaltemplate'}>
            Info
          </Link>
        ),
        description: 'Please fill the required details for the access requests',
        content: (
          <ApprovalWorkflowsAndTicketsEditor
            {...props}
            additionalMetadata={props.additionalMetadata}
            setAdditionalMetadata={props.setAdditionalMetadata}
          />
        ),
      });
    }
    steps.push({
      title: 'Additional details',
      description: 'Provide the following details, then submit the access requests.',
      content: (
        <AdditionalDetailsEditor
          {...props}
          useCase={props.useCase}
          setUseCase={props.setUseCase}
          dataConsumerType={props.dataConsumerType}
          setDataConsumerType={props.setDataConsumerType}
        />
      ),
    });
    setWizardSteps(props.cartItems.length > 0 ? steps : []);
  }, [props.additionalMetadata, props.cartItems, props.useCase, props.dataConsumerType, validationErrorMessage]);

  const WIZARD_I18N_STRINGS = {
    stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
    collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
    skipToButtonLabel: (step) => `Skip to ${step.title}`,
    navigationAriaLabel: 'Steps',
    cancelButton: 'Cancel',
    previousButton: 'Previous',
    nextButton: wizardSteps[activeStepIndex]?.title == 'Data usage agreements' ? 'Save and continue' : 'Next',
    submitButton: 'Request access',
    optional: 'optional',
  };

  if (props.loading) {
    return <Spinner />;
  }

  return (
    <>
      {props.cartItems.length > 0 && (
        <Wizard
          steps={wizardSteps}
          i18nStrings={WIZARD_I18N_STRINGS}
          activeStepIndex={activeStepIndex}
          onNavigate={async ({ detail }) => {
            // When navigating away from Validate page, do not allow if there are errors present.
            if (wizardSteps[activeStepIndex].title == 'Validate pre-requisites') {
              if (!!validationErrorMessage && validationErrorMessage.length > 0) {
                // if some error message is present (at least one resource has overall status = Failed), then do not navigate
                return;
              }
            }
            // Override default behavior for request context editor
            if (wizardSteps[activeStepIndex].title == 'Data usage agreements') {
              // Block navigation while validation is in progress to prevent duplicate D3A calls.
              if (requestContextDetailComponentRef.current?.isValidating?.()) return;
              // When navigating away from request context editor, save the request context.
              setSaveRequestContextLoading(true);
              if (requestContextDetailComponentRef.current) {
                const { hasErrors, cause } = await requestContextDetailComponentRef.current.save(); // Call the exposed function using forwardRef
                setSaveRequestContextLoading(false);
                // do not navigate if there are errors! Flashbar error is displayed on the page.
                if (hasErrors) return;
              }
              setSaveRequestContextLoading(false);
            }
            setActiveStepIndex(detail.requestedStepIndex);
          }}
          onCancel={props.onCancel}
          onSubmit={props.onSubmitAccessRequest}
          submitButtonText={'Request access'}
          isLoadingNextStep={props.requestSending || saveRequestContextLoading}
          allowSkipTo={false}
        />
      )}
    </>
  );
};
